/* eslint-disable indent */

import {IconMenu2} from "@tabler/icons-react";
import {Layout, Space} from "@ui";
import PropTypes from "prop-types";
import {useCallback, useState} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import GamanzaOnSiteHost from "@/components/GamanzaOnSiteHost";
import useGetRefParams from "@/hooks/useGetRefParams";
import useIsMobile from "@/hooks/useIsMobile";
import CustomNavbar from "../components/CustomNavbar";
import {openMobileSidebar} from "../components/FloatingMenu/mobileSidebarEvents";
import PremiumFooter from "../components/Footer/Footer";
import ScrollToTop from "../components/ScrollToTop";
import ThemeToggle from "../components/ThemeToggle/ThemeToggle";
import {useAuthStore} from "../store/useAuth.store";
import {useUserSupport} from "../store/useUserSupport.store";
import {useBrandTitle, useLogo, useMonogram} from "../util/logoUtils";
import {MenuVertical} from "./components/BaseMenu/arrayMenu";
import ButtonsNavbar from "./components/ButtonsNavbar";
import Wallet from "./components/Wallet";
import navStyles from "./Layout.module.css";

const {Content, Footer} = Layout;

const LOut = ({children}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false); //responsive sidebar logo
  const logo = useLogo();
  const monogram = useMonogram();
  const brandTitle = useBrandTitle();

  useGetRefParams();

  const menuVertical = MenuVertical();
  const {t} = useTranslation();

  const widthIsMajor1200 = !useIsMobile({breakpoint: 1200});
  const widthIsMajor800 = !useIsMobile({breakpoint: 800});

  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const handleSupportModal = useUserSupport(state => state.handleSupportModal);

  const authStatus = useAuthStore(state => state.authStatus);

  const selected = location.pathname.replace(/\/dashboard\/?/, "") || "index";
  const open = selected.split("/")[0];

  const isBingoRoute = location.pathname.startsWith("/bingo");
  const isTripletaRoute = location.pathname.startsWith("/lottery/tripleta");
  const hideFooter =
    location.pathname.startsWith("/games/") || location.pathname === "/horses" || isBingoRoute || isTripletaRoute;
  const isSportsRoute = location.pathname.startsWith("/sports");

  const onSelect = useCallback(
    ({key}) => {
      if (key === "/support") {
        handleSupportModal();
        return;
      }

      const publicPaths = ["/", "/testimonios", "/news"];
      if (authStatus !== "authenticated" && !publicPaths.includes(key)) {
        useAuthStore.getState().handleAuthForm("login");
        handleShowAuthModal();
        return;
      }

      navigate(key);
    },
    [navigate, authStatus, handleSupportModal]
  );

  return (
    <>
      <a
        href="#main-content"
        className={navStyles.skipToContent}
      >
        {t("skipToContent", "Skip to main content")}
      </a>
      <Layout
        className={`${navStyles.mainLayout}${isSportsRoute ? ` ${navStyles.mainLayoutSports}` : ""}${location.pathname.startsWith("/lottery/tripleta") ? ` ${navStyles.mainLayoutTripleta}` : ""}`}
        style={{
          margin:
            collapsed || !widthIsMajor800 || (!widthIsMajor1200 && (authStatus !== "authenticated" || widthIsMajor800))
              ? "0"
              : "0",
          padding: "0",
        }}
      >
        {/* Desktop Sidebar */}
        {widthIsMajor1200 && (
          <CustomNavbar
            items={menuVertical}
            onSelect={onSelect}
            collapsed={collapsed}
            onCollapse={() => setCollapsed(!collapsed)}
            mode="vertical"
            logo={logo}
            monogram={monogram}
            onLogoClick={() => navigate("/")}
            style={{
              boxShadow: "0 88px 8px rgba(0, 0, 0, 0.4)",
              overflowY: "auto",
            }}
          />
        )}

        <Layout style={{minHeight: "100dvh", marginLeft: widthIsMajor1200 ? (collapsed ? 78 : 260) : 0}}>
          {/* Mobile/Tablet Header - Show for unauthenticated users OR authenticated users in tablet/mobile range */}
          {!widthIsMajor1200 && (authStatus !== "authenticated" || widthIsMajor800) && (
            <header className={navStyles.mobileHeader}>
              <div className={navStyles.headerInner}>
                {/* Monogram — Home (tablet) / Menu button (mobile) */}
                {!widthIsMajor800 ? (
                  <button
                    type="button"
                    className={navStyles.mobileMenuButton}
                    onClick={openMobileSidebar}
                    aria-label={t("floatingMenuMenu")}
                  >
                    <IconMenu2
                      size={24}
                      stroke={2}
                    />
                  </button>
                ) : (
                  <Link
                    to="/"
                    className={navStyles.mobileMonogramLink}
                    aria-label={t("goHome", {defaultValue: "Ir al inicio"})}
                    onClick={() => {
                      window.scrollTo({top: 0, behavior: "smooth"});
                    }}
                  >
                    <img
                      src={monogram}
                      alt=""
                      className={navStyles.mobileMonogram}
                      draggable={false}
                    />
                  </Link>
                )}
                {/* Hamburger Menu */}
                {widthIsMajor800 && !widthIsMajor1200 && (
                  <CustomNavbar
                    items={menuVertical}
                    onSelect={onSelect}
                    mode="horizontal"
                    style={{background: "transparent"}}
                    defaultOpenKeys={[open]}
                    overflowedIndicator={
                      <span
                        role="img"
                        aria-label={t("floatingMenuMenu", "Menu")}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          color: "var(--text-primary)",
                          fontSize: 25,
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <IconMenu2
                          size={24}
                          stroke={2}
                          color="var(--text-primary)"
                        />
                      </span>
                    }
                  />
                )}
                {/* User Actions */}
                <Space style={{marginLeft: "auto"}}>
                  {authStatus !== "authenticated" ? (
                    <>
                      <ThemeToggle />
                      <SecondaryButton
                        onClick={() => {
                          useAuthStore.getState().handleAuthForm("login");
                          handleShowAuthModal();
                        }}
                      >
                        {t("homeDisplayLoginBtn")}
                      </SecondaryButton>
                      <PrimaryButton
                        onClick={() => {
                          useAuthStore.getState().handleAuthForm("registration");
                          handleShowAuthModal();
                        }}
                      >
                        {t("homeDisplayRegisterBtn")}
                      </PrimaryButton>
                    </>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Wallet />
                    </div>
                  )}
                </Space>
              </div>
            </header>
          )}

          {/* Desktop Header */}
          {widthIsMajor1200 && (
            <header className={navStyles.desktopHeader}>
              <ErrorBoundary fallback={<h2>Something went wrong with the Match Result</h2>} />
              <div className={navStyles.profilePart}>
                {!widthIsMajor1200 && (
                  <img
                    src={logo}
                    alt={brandTitle}
                    style={{
                      width: "50px",
                      margin: "0 auto",
                      cursor: "pointer",
                      transition: "all 0.3s ease-in-out",
                    }}
                    onClick={() => navigate("/")}
                  />
                )}
                <ButtonsNavbar sidebarCollapsed={collapsed} />
                <div className={navStyles.headerSpacer} />
                {authStatus !== "authenticated" && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginRight: 30,
                    }}
                    className={navStyles.headerAuthButtons}
                  >
                    <ThemeToggle />
                    <SecondaryButton
                      onClick={() => {
                        useAuthStore.getState().handleAuthForm("login");
                        handleShowAuthModal();
                      }}
                    >
                      {t("homeDisplayLoginBtn")}
                    </SecondaryButton>
                    <PrimaryButton
                      onClick={() => {
                        useAuthStore.getState().handleAuthForm("registration");
                        handleShowAuthModal();
                      }}
                    >
                      {t("homeDisplayRegisterBtn")}
                    </PrimaryButton>
                  </div>
                )}
                {authStatus === "authenticated" && (
                  <div className={navStyles.walletWrapper}>
                    <Wallet />
                  </div>
                )}
              </div>
            </header>
          )}

          <Content
            id="main-content"
            className={navStyles.contentArea}
            role="main"
            style={{
              padding: "0",
              margin: "0",
              minHeight: "calc(100dvh - 64px)",
              paddingTop: !widthIsMajor1200 && authStatus === "authenticated" && !widthIsMajor800 ? "0" : "inherit",
              paddingBottom:
                isSportsRoute || isBingoRoute || widthIsMajor800
                  ? "0"
                  : "calc(64px + env(safe-area-inset-bottom, 16px))",
            }}
          >
            <div
              id="layout-content-portal"
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
            <div style={{position: "relative", zIndex: 1}}>{children}</div>
          </Content>
          {!hideFooter && (
            <Footer
              style={{
                textAlign: "center",
                color: "var(--text-primary)",
                background: "var(--header-bg)",
                position: "relative",
                zIndex: 1,
                padding: 0,
              }}
            >
              <PremiumFooter />
            </Footer>
          )}
          <ScrollToTop />
          <GamanzaOnSiteHost />
        </Layout>
      </Layout>
    </>
  );
};

LOut.propTypes = {
  children: PropTypes.node,
};

export default LOut;
